/**
 * Money display formatting. The API ships MoneyAmount as strings with
 * 4 fractional digits (preserves cents-of-cents for calculation fidelity).
 * The UI shows 2 fractional digits with thousand separators + currency.
 *
 * We NEVER parse MoneyAmount via Number() for math — only for display.
 * decimal.js handles arithmetic server-side; the FE is display-only.
 */

import type { CurrencyCode } from '@kora/types';

/**
 * Currency symbol mapping. Prefix-positioned for consistency with
 * English/French finance UI convention. CDF uses "FC" (franc congolais)
 * which is post-positioned in francophone markets, but we standardize
 * on prefix for a single readable rendering.
 */
const CURRENCY_SYMBOL: Record<CurrencyCode, string> = {
  NGN: '\u20A6', // ₦
  GHS: 'GH\u20B5', // GH₵
  CDF: 'FC',
  USD: '$',
};

/**
 * Format a MoneyAmount string for display.
 *
 * @example
 *   formatMoney('500000.0000', 'NGN')  → '₦500,000.00'
 *   formatMoney('1278.6875', 'GHS')    → 'GH₵1,278.69'   (banker's rounding to 2dp)
 *   formatMoney('1500000.0000', 'CDF') → 'FC 1,500,000.00'
 *   formatMoney('37.1212', 'USD')      → '$37.12'
 *   formatMoney('-42.5', 'NGN')        → '-₦42.50'
 *
 * Locale: always en-US for number formatting so the thousand separator
 * is a comma regardless of browser locale. This keeps display consistent
 * across the NG/GH/CD markets we support (all of which are comfortable
 * reading Anglo number formatting).
 */
export function formatMoney(amount: string, currency: CurrencyCode): string {
  const num = Number(amount);
  if (!Number.isFinite(num)) {
    // Invalid input → fall through to raw display; never throw from
    // a display function.
    return `${amount} ${currency}`;
  }
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(abs);
  const symbol = CURRENCY_SYMBOL[currency] ?? currency;
  // CDF + multi-char symbols (GH₵) get a space after the symbol for
  // readability; single-char symbols (₦, $) render flush.
  const separator = symbol.length > 1 ? ' ' : '';
  return `${sign}${symbol}${separator}${formatted}`;
}

/**
 * Format just a number (no currency), useful for counts + percentages.
 */
export function formatNumber(amount: string | number): string {
  const num = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(num)) return String(amount);
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
}

/** Strip the time component from an ISO datetime for compact display. */
export function formatDate(isoDatetime: string): string {
  return isoDatetime.slice(0, 10);
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Human-readable payroll period from a (year, 1-12 month) pair, e.g.
 * (2026, 6) → "June 2026". Used by the compliance-reports pages where
 * the period is stored as separate periodYear / periodMonth ints.
 */
export function formatPeriod(year: number, month: number): string {
  const name = MONTH_NAMES[month - 1] ?? `Month ${month}`;
  return `${name} ${year}`;
}

/**
 * Compact byte-count for file sizes. Used by both the admin
 * documents section and the employee /me/documents page.
 *
 * Three tiers (B / KB / MB) are sufficient — uploaded documents
 * cap at EMPLOYEE_DOCUMENT_MAX_BYTES (10 MB), so GB never appears.
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
